import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';

import { createRulesDriver, fetchRulesDrivers, updateRulesDriver } from '../../features/rulesEngine/api';
import type { RulesDriver } from '../../features/rulesEngine/types';
import RulesSectionCard from './RulesSectionCard';

export default function DriversPanel() {
  const theme = useTheme();
  const [drivers, setDrivers] = useState<RulesDriver[]>([]);
  const [name, setName] = useState('');
  const [externalId, setExternalId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setDrivers(await fetchRulesDrivers());
  }, []);

  useEffect(() => {
    void reload().catch((e) => setError(e instanceof Error ? e.message : 'Failed to load drivers'));
  }, [reload]);

  const activeCount = useMemo(() => drivers.filter((d) => d.status === 'active').length, [drivers]);
  const inactiveCount = drivers.length - activeCount;

  const onCreate = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      await createRulesDriver({
        display_name: name.trim(),
        external_driver_id: externalId.trim() || undefined,
      });
      setName('');
      setExternalId('');
      setMessage('Driver added.');
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  const onToggleStatus = async (d: RulesDriver) => {
    setError(null);
    try {
      await updateRulesDriver(d.id, {
        status: d.status === 'active' ? 'inactive' : 'active',
      });
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    }
  };

  return (
    <Box data-testid="rules.drivers.panel" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Drivers
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Maintain the driver pool used by duty planning and Advance Rostering. Inactive drivers stay in
          history but are not offered for new assignments.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {message && (
        <Alert
          severity="success"
          icon={<CheckCircleOutlineIcon fontSize="inherit" />}
          onClose={() => setMessage(null)}
        >
          {message}
        </Alert>
      )}

      <RulesSectionCard
        icon={<PersonAddAltOutlinedIcon />}
        title="Add driver"
        description="Display name is required. External id is optional (payroll / TMS reference)."
      >
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr auto' },
            alignItems: 'start',
          }}
        >
          <TextField
            size="small"
            label="Display name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            data-testid="rules.drivers.name-input"
          />
          <TextField
            size="small"
            label="External driver id (optional)"
            value={externalId}
            onChange={(e) => setExternalId(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <BadgeOutlinedIcon
                  fontSize="small"
                  sx={{ mr: 1, color: theme.palette.text.secondary }}
                />
              ),
            }}
            data-testid="rules.drivers.external-id-input"
          />
          <Button
            variant="contained"
            onClick={() => void onCreate()}
            disabled={busy || !name.trim()}
            startIcon={<PersonAddAltOutlinedIcon />}
            sx={{ height: 40, whiteSpace: 'nowrap' }}
            data-testid="rules.drivers.create-btn"
          >
            Add driver
          </Button>
        </Box>
      </RulesSectionCard>

      <RulesSectionCard
        icon={<GroupsOutlinedIcon />}
        title="Driver roster"
        description="Active drivers can be assigned to duties and roster proposals."
        action={
          drivers.length > 0 ? (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              <Chip
                size="small"
                color="success"
                variant="outlined"
                icon={<CheckCircleOutlineIcon />}
                label={`${activeCount} active`}
              />
              {inactiveCount > 0 && (
                <Chip
                  size="small"
                  variant="outlined"
                  icon={<PauseCircleOutlineIcon />}
                  label={`${inactiveCount} inactive`}
                />
              )}
            </Stack>
          ) : null
        }
      >
        <Table size="small" data-testid="rules.drivers.table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>External id</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {drivers.map((d) => (
              <TableRow key={d.id}>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {d.display_name}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    color={d.external_driver_id ? 'text.primary' : 'text.secondary'}
                    sx={{ fontFamily: d.external_driver_id ? 'monospace' : undefined }}
                  >
                    {d.external_driver_id || '—'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={d.status === 'active' ? 'success' : 'default'}
                    icon={
                      d.status === 'active' ? (
                        <CheckCircleOutlineIcon />
                      ) : (
                        <PauseCircleOutlineIcon />
                      )
                    }
                    label={d.status}
                  />
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => void onToggleStatus(d)}>
                    {d.status === 'active' ? 'Deactivate' : 'Activate'}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!drivers.length && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">
                    No drivers yet. Add one above to start planning duties.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </RulesSectionCard>
    </Box>
  );
}
